import { memo, useDeferredValue, useEffect, useRef, useState } from 'react';

// Side-effect import: registers the WebGL volume-rendering backend.
import '@kitware/vtk.js/Rendering/Profiles/Volume';
import vtkGenericRenderWindow from '@kitware/vtk.js/Rendering/Misc/GenericRenderWindow';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';

import type { ManifestV2, Volume } from './types';
import { getVolume3D } from './volume3d';

// --- Palette (matches CBCTViewer) -------------------------------------------
const WHITE = '#ffffff';
const BORDER = '#e0e6eb';
const INK = '#1c2734';
const MUTED = '#5b6774';
const PANE_BG = '#000000';
const PANE_BORDER = '#cfd6dd';
const MONO = "ui-monospace,'SF Mono','Cascadia Code',monospace";

type CompId = 'bone' | 'teeth' | 'soft';

/** HU → (opacity, color) transfer-function components.
 *  Opacity points are [HU, alpha]; color points are [HU, r, g, b].
 *  Unlike a preset picker, these are layered together — each component gets
 *  its own density slider and they sum into one transfer function. */
const COMPONENTS: Record<
    CompId,
    {
        label: string;
        accent: string;
        opacity: [number, number][];
        color: [number, number, number, number][];
    }
> = {
    // The classic translucent yellow-ivory bone view.
    bone: {
        label: 'Bone',
        accent: '#fbbf24',
        opacity: [
            [-1000, 0],
            [250, 0],
            [450, 0.1],
            [1100, 0.35],
            [3000, 0.7]
        ],
        color: [
            [250, 0.55, 0.38, 0.18],
            [700, 0.88, 0.74, 0.46],
            [1300, 1.0, 0.94, 0.78],
            [3000, 1.0, 1.0, 0.95]
        ]
    },
    // Higher threshold: enamel/dentin pop, thin bone fades.
    teeth: {
        label: 'Teeth',
        accent: '#22b8cf',
        opacity: [
            [-1000, 0],
            [900, 0],
            [1250, 0.2],
            [2000, 0.55],
            [3000, 0.85]
        ],
        color: [
            [900, 0.72, 0.62, 0.4],
            [1500, 0.98, 0.95, 0.85],
            [3000, 1.0, 1.0, 1.0]
        ]
    },
    // Skin/soft-tissue surface (facial profile).
    soft: {
        label: 'Soft tissue',
        accent: '#fb7185',
        opacity: [
            [-1000, 0],
            [-620, 0],
            [-380, 0.16],
            [-100, 0.2],
            [200, 0.22],
            [3000, 0.24]
        ],
        color: [
            [-620, 0.6, 0.4, 0.29],
            [-200, 0.88, 0.66, 0.5],
            [400, 0.95, 0.8, 0.68],
            [3000, 1.0, 0.95, 0.9]
        ]
    }
};

const ORDER: CompId[] = ['bone', 'teeth', 'soft'];

// Per-component density. 50 = component's opacities as authored; 0 = fully off.
const DEFAULTS: Record<CompId, number> = { bone: 50, teeth: 45, soft: 0 };

/** Density slider 0..100 → opacity gain 0×..2× (linear, 1× at midpoint, 0 = off). */
function densityGain(v: number): number {
    return v / 50;
}

/** Piecewise-linear sample of an [x, y] table, clamped past the ends. */
function interpAt(pts: [number, number][], x: number): number {
    if (x <= pts[0][0]) return pts[0][1];
    const last = pts[pts.length - 1];
    if (x >= last[0]) return last[1];
    for (let i = 1; i < pts.length; i++) {
        const [x0, y0] = pts[i - 1];
        const [x1, y1] = pts[i];
        if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
    return last[1];
}

/** Piecewise-linear sample of an [x, r, g, b] table, clamped past the ends. */
function interpColorAt(
    pts: [number, number, number, number][],
    x: number
): [number, number, number] {
    if (x <= pts[0][0]) return [pts[0][1], pts[0][2], pts[0][3]];
    const last = pts[pts.length - 1];
    if (x >= last[0]) return [last[1], last[2], last[3]];
    for (let i = 1; i < pts.length; i++) {
        const [x0, r0, g0, b0] = pts[i - 1];
        const [x1, r1, g1, b1] = pts[i];
        if (x <= x1) {
            const t = (x - x0) / (x1 - x0);
            return [r0 + (r1 - r0) * t, g0 + (g1 - g0) * t, b0 + (b1 - b0) * t];
        }
    }
    return [last[1], last[2], last[3]];
}

interface VtkParts {
    grw: ReturnType<typeof vtkGenericRenderWindow.newInstance>;
    ofun: ReturnType<typeof vtkPiecewiseFunction.newInstance>;
    cfun: ReturnType<typeof vtkColorTransferFunction.newInstance>;
    renderer: ReturnType<ReturnType<typeof vtkGenericRenderWindow.newInstance>['getRenderer']>;
}

/**
 * GPU ray-cast volume rendering (vtk.js) of a downsampled copy of the HU volume.
 * The transfer function maps HU → color/opacity directly, so presets are
 * expressed in the same physical units as the 2D views' window/level.
 */
export const Volume3DPanel = memo(function Volume3DPanel({
    volume,
    manifest,
    flipVertical = false
}: {
    volume: Volume;
    manifest: ManifestV2;
    /** Reverses the superior/inferior (z) orientation — see `displayZ` in volume.ts. */
    flipVertical?: boolean;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const vtkRef = useRef<VtkParts | null>(null);
    const [density, setDensity] = useState<Record<CompId, number>>(DEFAULTS);
    // Sliders update `density` immediately (responsive thumb); the transfer-function
    // rebuild below runs off this deferred copy so dragging doesn't rebuild per event.
    const dDensity = useDeferredValue(density);
    const [ready, setReady] = useState(false);
    const [controlsOpen, setControlsOpen] = useState(false);

    // Build the scene once per mount (the downsampled volume is cached across mounts).
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        let disposed = false;
        let parts: VtkParts | null = null;

        // Defer one tick so the "preparing" placeholder paints before the
        // downsample + texture upload block the main thread.
        const timer = setTimeout(() => {
            if (disposed) return;
            const v3 = getVolume3D(volume, flipVertical);
            const { z: sz, y: sy, x: sx } = manifest.spacing_mm;

            const imageData = vtkImageData.newInstance();
            // Our Int16Array is x-fastest, then y, then z — exactly vtk's memory order.
            imageData.setDimensions([v3.nx, v3.ny, v3.nz]);
            imageData.setSpacing([sx * v3.factor, sy * v3.factor, sz * v3.factor]);
            imageData
                .getPointData()
                .setScalars(
                    vtkDataArray.newInstance({ name: 'HU', values: v3.data, numberOfComponents: 1 })
                );

            const grw = vtkGenericRenderWindow.newInstance({ background: [0, 0, 0] });
            grw.setContainer(container);

            const mapper = vtkVolumeMapper.newInstance();
            mapper.setInputData(imageData);
            // World units are mm; sample at ~the reduced voxel pitch. Raise the per-ray
            // step cap so a full head's worth of samples isn't clipped (the default
            // 1000 is exceeded by a ~150 mm skull at this pitch).
            mapper.setSampleDistance(Math.min(sx, sy, sz) * v3.factor);
            mapper.setMaximumSamplesPerRay(4000);

            const actor = vtkVolume.newInstance();
            actor.setMapper(mapper);

            const ofun = vtkPiecewiseFunction.newInstance();
            const cfun = vtkColorTransferFunction.newInstance();
            const prop = actor.getProperty();
            prop.setRGBTransferFunction(0, cfun);
            prop.setScalarOpacity(0, ofun);
            prop.setInterpolationTypeToLinear();
            prop.setShade(true);
            prop.setAmbient(0.25);
            prop.setDiffuse(0.75);
            prop.setSpecular(0.25);
            prop.setSpecularPower(12);
            prop.setScalarOpacityUnitDistance(0, Math.min(sx, sy, sz) * v3.factor * 2);

            const renderer = grw.getRenderer();
            renderer.addVolume(actor);

            // Face the patient: camera anterior (−y in LPS), superior (+z) up.
            // With dop=+y, up=+z the screen-right axis is +x = patient left —
            // the standard frontal-view convention.
            const camera = renderer.getActiveCamera();
            const [cx, cy, cz] = imageData.getCenter();
            camera.setFocalPoint(cx, cy, cz);
            camera.setPosition(cx, cy - Math.max(v3.ny * sy * v3.factor * 2.2, 200), cz);
            camera.setViewUp(0, 0, 1);

            // Sync the canvas to the (now laid-out) container before framing, or
            // resetCamera fits to a stale default size and the volume renders tiny.
            grw.resize();
            renderer.resetCamera();
            grw.getRenderWindow().render();

            parts = { grw, ofun, cfun, renderer };
            vtkRef.current = parts;
            setReady(true);
        }, 30);

        // Keep the canvas in sync on layout changes. Re-frame only until the first
        // non-zero size is seen, so later resizes preserve the user's orientation.
        let framed = false;
        const observer = new ResizeObserver(() => {
            const current = vtkRef.current;
            if (!current) return;
            current.grw.resize();
            if (!framed && container.clientHeight > 0) {
                framed = true;
                current.renderer.resetCamera();
            }
            current.grw.getRenderWindow().render();
        });
        observer.observe(container);

        return () => {
            disposed = true;
            clearTimeout(timer);
            observer.disconnect();
            setReady(false);
            vtkRef.current = null;
            parts?.grw.delete();
        };
    }, [volume, manifest, flipVertical]);

    // Rebuild the combined transfer function whenever any density changes.
    // Opacity is the (clamped) sum of each active component's scaled curve;
    // since a sum of piecewise-linear functions is piecewise-linear with
    // breakpoints at the union of the inputs', evaluating at that union
    // reproduces the sum exactly. Color is blended by each component's
    // opacity contribution so the dominant tissue at a given HU wins.
    useEffect(() => {
        const parts = vtkRef.current;
        if (!parts || !ready) return;
        const { ofun, cfun, grw } = parts;

        const active = ORDER.filter((id) => dDensity[id] > 0);
        ofun.removeAllPoints();
        cfun.removeAllPoints();

        if (active.length === 0) {
            // Nothing visible — a flat, empty transfer function.
            ofun.addPoint(-1000, 0);
            ofun.addPoint(3000, 0);
            cfun.addRGBPoint(-1000, 0, 0, 0);
            cfun.addRGBPoint(3000, 0, 0, 0);
        } else {
            const hus = Array.from(
                new Set(active.flatMap((id) => COMPONENTS[id].opacity.map((p) => p[0])))
            ).sort((a, b) => a - b);

            for (const hu of hus) {
                let alpha = 0,
                    wr = 0,
                    wg = 0,
                    wb = 0,
                    wsum = 0;
                for (const id of active) {
                    const contrib =
                        interpAt(COMPONENTS[id].opacity, hu) * densityGain(dDensity[id]);
                    const [r, g, b] = interpColorAt(COMPONENTS[id].color, hu);
                    alpha += contrib;
                    wr += r * contrib;
                    wg += g * contrib;
                    wb += b * contrib;
                    wsum += contrib;
                }
                ofun.addPoint(hu, Math.min(1, alpha));
                if (wsum > 1e-6) cfun.addRGBPoint(hu, wr / wsum, wg / wsum, wb / wsum);
                else {
                    const [r, g, b] = interpColorAt(COMPONENTS.bone.color, hu);
                    cfun.addRGBPoint(hu, r, g, b);
                }
            }
        }

        grw.getRenderWindow().render();
    }, [dDensity.bone, dDensity.teeth, dDensity.soft, ready]);

    return (
        <div
            className="relative h-full w-full overflow-hidden"
            style={{ background: PANE_BG, border: `1px solid ${PANE_BORDER}`, borderRadius: 7 }}
        >
            <div ref={containerRef} className="absolute inset-0" />
            {!ready && (
                <div
                    className="absolute inset-0 grid place-items-center"
                    style={{ fontFamily: MONO, fontSize: 11, color: '#8894a0' }}
                >
                    preparing 3D volume…
                </div>
            )}

            <div
                className="pointer-events-none absolute"
                style={{
                    top: 9,
                    left: 11,
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.13em',
                    color: '#45c3d6'
                }}
            >
                3D VOLUME
            </div>

            {/* Controls toggle (top-right, over the render) */}
            <button
                onClick={() => setControlsOpen((v) => !v)}
                title={controlsOpen ? 'Hide volume controls' : 'Show volume controls'}
                aria-label={controlsOpen ? 'Hide volume controls' : 'Show volume controls'}
                className="absolute grid place-items-center"
                style={{
                    top: 8,
                    right: 44,
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: controlsOpen ? 'rgba(14,151,176,.24)' : 'rgba(255,255,255,.08)',
                    border: `1px solid ${controlsOpen ? 'rgba(143,208,220,.55)' : 'rgba(255,255,255,.16)'}`,
                    color: controlsOpen ? '#8fd0dc' : '#c6d0d8',
                    cursor: 'pointer'
                }}
            >
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                >
                    <line x1="4" y1="8" x2="20" y2="8" />
                    <circle cx="9" cy="8" r="2.2" fill="currentColor" stroke="none" />
                    <line x1="4" y1="16" x2="20" y2="16" />
                    <circle cx="15" cy="16" r="2.2" fill="currentColor" stroke="none" />
                </svg>
            </button>

            {/* Tissue-density controls — floating vertical panel on the left of the render */}
            {controlsOpen && (
                <div
                    className="absolute flex flex-col gap-3"
                    style={{
                        left: 11,
                        top: 44,
                        width: 158,
                        maxHeight: 'calc(100% - 55px)',
                        overflowY: 'auto',
                        padding: '11px 13px',
                        background: 'rgba(247,249,251,.95)',
                        border: `1px solid ${BORDER}`,
                        borderRadius: 9,
                        boxShadow: '0 8px 24px rgba(24,40,56,.28)'
                    }}
                >
                    <span style={{ fontWeight: 600, fontSize: 12, color: INK }}>
                        Volume rendering
                    </span>
                    <div className="flex flex-col gap-3">
                        {ORDER.map((id) => (
                            <label
                                key={id}
                                className="flex flex-col gap-1"
                                style={{ fontSize: 11.5, color: MUTED }}
                            >
                                <span className="flex items-center gap-2">
                                    <span
                                        className="inline-block flex-shrink-0"
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: COMPONENTS[id].accent
                                        }}
                                    />
                                    <span className="flex-1 min-w-0">{COMPONENTS[id].label}</span>
                                    <span style={{ fontFamily: MONO, fontSize: 11, color: INK }}>
                                        {density[id]}
                                    </span>
                                </span>
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={density[id]}
                                    onChange={(e) =>
                                        setDensity((d) => ({ ...d, [id]: Number(e.target.value) }))
                                    }
                                    className="w-full cursor-pointer"
                                    style={{ accentColor: COMPONENTS[id].accent }}
                                />
                            </label>
                        ))}
                    </div>

                    <button
                        onClick={() => setDensity(DEFAULTS)}
                        className="self-end"
                        style={{
                            padding: '5px 11px',
                            borderRadius: 7,
                            background: WHITE,
                            border: `1px solid ${BORDER}`,
                            color: MUTED,
                            fontSize: 11,
                            cursor: 'pointer'
                        }}
                    >
                        Reset
                    </button>
                </div>
            )}
        </div>
    );
});

export default Volume3DPanel;
